import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Input, Badge } from '@jaylabs/ui';
import { useColorMode } from '../components/ui/color-mode';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../utils/apiUrl';
import UserBadgeBar from '../components/UserBadgeBar';
import './Settings.css';

const TABS = [
  { id: 'profile', label: '👤 Profile' },
  { id: 'security', label: '🔐 Security' },
  { id: 'appearance', label: '🎨 Appearance' },
  { id: 'task-settings', label: '⚙️ Task Settings' },
];

function Settings() {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const { colorMode, toggleColorMode } = useColorMode();
  const [activeTab, setActiveTab] = useState('profile');
  const [toastMsg, setToastMsg] = useState(null);

  const showToast = (type, message) => {
    setToastMsg({ type, message });
    setTimeout(() => setToastMsg(null), 2500);
  };

  // Categories
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState('');
  const [confirmDeleteCategory, setConfirmDeleteCategory] = useState(null);

  // Statuses
  const [statuses, setStatuses] = useState([
    'pending', 'in-progress', 'completed', 'cancelled'
  ]);
  const [newStatus, setNewStatus] = useState('');
  const [confirmDeleteStatus, setConfirmDeleteStatus] = useState(null);

  // Tags
  const [tags, setTags] = useState([]);
  const [newTag, setNewTag] = useState('');
  const [loadingTags, setLoadingTags] = useState(true);
  const [confirmDeleteTag, setConfirmDeleteTag] = useState(null);

  // Profile Edit
  const [displayName, setDisplayName] = useState(user?.display_name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [profileError, setProfileError] = useState('');
  const [profileSuccess, setProfileSuccess] = useState('');
  const [profileLoading, setProfileLoading] = useState(false);

  // Password Change
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);

  // Two-factor auth
  const [twoFAEnabled, setTwoFAEnabled] = useState(user?.two_factor_enabled ?? false);
  const [twoFASecret, setTwoFASecret] = useState('');
  const [twoFAQr, setTwoFAQr] = useState('');
  const [twoFACode, setTwoFACode] = useState('');
  const [twoFAStatus, setTwoFAStatus] = useState('');
  const [twoFAError, setTwoFAError] = useState('');
  const [twoFALoading, setTwoFALoading] = useState(false);

  const storedBg = localStorage.getItem('dashboardBg') || '/backgrounds/Background-2-1024x682.jpg';

  useEffect(() => {
    fetchUserTags();
    fetchCategories();
    if (user) {
      setDisplayName(user.display_name || '');
      setEmail(user.email || '');
      setTwoFAEnabled(user.two_factor_enabled ?? false);
    }
  }, [user]);

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/categories`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) setCategories(data.categories || []);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const fetchUserTags = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/tasks`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        const allTags = new Set();
        data.tasks.forEach(task => {
          if (task.tags && Array.isArray(task.tags)) task.tags.forEach(tag => allTags.add(tag));
        });
        setTags(Array.from(allTags));
      }
    } catch (err) {
      console.error('Error fetching tags:', err);
    } finally {
      setLoadingTags(false);
    }
  };

  const handleAddCategory = async () => {
    if (!newCategory.trim()) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/categories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ name: newCategory.trim() })
      });
      const data = await res.json();
      if (res.ok) {
        setCategories(prev => [...prev, data.category]);
        setNewCategory('');
        showToast('success', 'Category added');
      } else {
        showToast('error', data.message || 'Failed to add category');
      }
    } catch (err) {
      console.error('Error adding category:', err);
      showToast('error', 'Error adding category');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (confirmDeleteCategory !== categoryId) {
      setConfirmDeleteCategory(categoryId);
      return;
    }
    setConfirmDeleteCategory(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/categories/${categoryId}/toggle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ is_active: false })
      });
      if (res.ok) {
        setCategories(prev => prev.map(c => c.id === categoryId ? { ...c, is_active: false } : c));
        showToast('success', 'Category deactivated');
      } else {
        const data = await res.json();
        showToast('error', data.message || 'Failed to deactivate category');
      }
    } catch (err) {
      console.error('Error deactivating category:', err);
      showToast('error', 'Error deactivating category');
    }
  };

  const handleAddStatus = () => {
    if (newStatus.trim() && !statuses.includes(newStatus.toLowerCase().trim())) {
      const updated = [...statuses, newStatus.toLowerCase().trim()];
      setStatuses(updated);
      setNewStatus('');
      localStorage.setItem('userStatuses', JSON.stringify(updated));
      showToast('success', 'Status added');
    }
  };

  const handleDeleteStatus = (status) => {
    if (confirmDeleteStatus !== status) {
      setConfirmDeleteStatus(status);
      return;
    }
    setConfirmDeleteStatus(null);
    const updated = statuses.filter(s => s !== status);
    setStatuses(updated);
    localStorage.setItem('userStatuses', JSON.stringify(updated));
    showToast('info', 'Status removed');
  };

  const handleAddTag = () => {
    if (newTag.trim() && !tags.includes(newTag.trim())) {
      setTags([...tags, newTag.trim()]);
      setNewTag('');
      showToast('success', 'Tag added');
    }
  };

  const handleDeleteTag = (tag) => {
    if (confirmDeleteTag !== tag) {
      setConfirmDeleteTag(tag);
      return;
    }
    setConfirmDeleteTag(null);
    setTags(tags.filter(t => t !== tag));
    showToast('info', 'Tag removed');
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setProfileError('');
    setProfileSuccess('');
    if (!displayName.trim() && !email.trim()) {
      setProfileError('At least one field must be filled');
      return;
    }
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setProfileError('Invalid email format');
      return;
    }
    try {
      setProfileLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          display_name: displayName.trim() || undefined,
          email: email.trim() || undefined
        })
      });
      const data = await response.json();
      if (response.ok) {
        setProfileSuccess('Profile updated successfully!');
        showToast('success', 'Profile updated');
        updateUser(data.profile);
      } else {
        setProfileError(data.message || 'Failed to update profile');
        showToast('error', data.message || 'Failed to update profile');
      }
    } catch (err) {
      setProfileError('Error connecting to server');
      console.error('Error:', err);
    } finally {
      setProfileLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('All fields are required');
      return;
    }
    if (newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters long');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }
    try {
      setPasswordLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ currentPassword, newPassword })
      });
      const data = await response.json();
      if (response.ok) {
        setPasswordSuccess('Password changed successfully!');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        showToast('success', 'Password changed');
      } else {
        setPasswordError(data.message || 'Failed to change password');
      }
    } catch (err) {
      setPasswordError('Error connecting to server');
      console.error('Error:', err);
    } finally {
      setPasswordLoading(false);
    }
  };

  const startTwoFASetup = async () => {
    const token = localStorage.getItem('token');
    if (!token) { setTwoFAError('You need to be logged in.'); return; }
    setTwoFALoading(true);
    setTwoFAError('');
    setTwoFAStatus('');
    try {
      const response = await fetch(`${API_URL}/api/auth/2fa/setup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        setTwoFASecret(data.secret || '');
        setTwoFAQr(data.qrDataUrl || '');
        setTwoFAStatus('Scan the QR code with your authenticator app, then enter the 6-digit code to enable.');
      } else {
        setTwoFAError(data.message || 'Unable to start 2FA setup.');
      }
    } catch (err) {
      console.error('Error starting 2FA setup:', err);
      setTwoFAError('Error connecting to server.');
    } finally {
      setTwoFALoading(false);
    }
  };

  const handleVerifyTwoFA = async () => {
    const token = localStorage.getItem('token');
    if (!token) { setTwoFAError('You need to be logged in.'); return; }
    if (!twoFACode.trim()) { setTwoFAError('Enter the code from your authenticator app.'); return; }
    setTwoFALoading(true);
    setTwoFAError('');
    setTwoFAStatus('');
    try {
      const response = await fetch(`${API_URL}/api/auth/2fa/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ token: twoFACode.trim() })
      });
      const data = await response.json();
      if (response.ok) {
        setTwoFAEnabled(true);
        setTwoFAStatus('Two-factor authentication is enabled.');
        setTwoFASecret('');
        setTwoFAQr('');
        setTwoFACode('');
        updateUser({ two_factor_enabled: true });
        showToast('success', 'Two-factor enabled');
      } else {
        setTwoFAError(data.message || 'Invalid code. Please try again.');
      }
    } catch (err) {
      console.error('Error verifying 2FA:', err);
      setTwoFAError('Error connecting to server.');
    } finally {
      setTwoFALoading(false);
    }
  };

  const handleDisableTwoFA = async () => {
    const token = localStorage.getItem('token');
    if (!token) { setTwoFAError('You need to be logged in.'); return; }
    setTwoFALoading(true);
    setTwoFAError('');
    setTwoFAStatus('');
    try {
      const response = await fetch(`${API_URL}/api/auth/2fa/disable`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        setTwoFAEnabled(false);
        setTwoFASecret('');
        setTwoFAQr('');
        setTwoFACode('');
        updateUser({ two_factor_enabled: false });
        showToast('info', 'Two-factor disabled');
      } else {
        setTwoFAError(data.message || 'Failed to disable 2FA.');
      }
    } catch (err) {
      console.error('Error disabling 2FA:', err);
      setTwoFAError('Error connecting to server.');
    } finally {
      setTwoFALoading(false);
    }
  };

  return (
    <div
      className="dd-settings-page"
      style={{
        backgroundImage: `linear-gradient(180deg, rgba(255,255,255,0.7), rgba(255,255,255,0.7)), url(${storedBg})`
      }}
    >
      {toastMsg && (
        <div className={`dd-toast dd-toast-${toastMsg.type}`}>
          {toastMsg.message}
        </div>
      )}

      <div className="dd-hstack dd-px-4 dd-pt-4" style={{ justifyContent: 'flex-end' }}>
        <UserBadgeBar />
      </div>

      <div className="dd-container" style={{ paddingTop: 'var(--dd-space-6)' }}>
        {/* Header */}
        <div className="dd-hstack dd-mb-6" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 'var(--dd-space-4)' }}>
          <h1 className="dd-heading-xl">Settings</h1>
          <Button variant="outline" size="lg" onClick={() => navigate('/dashboard')}>
            Back to Dashboard
          </Button>
        </div>

        {/* Tab bar */}
        <div className="dd-settings-tabs" role="tablist" aria-label="Settings sections">
          {TABS.map(tab => (
            <button
              key={tab.id}
              role="tab"
              aria-selected={activeTab === tab.id}
              className={`dd-settings-tab${activeTab === tab.id ? ' dd-settings-tab-active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Profile tab */}
        {activeTab === 'profile' && (
          <div className="dd-card dd-mt-6">
            <h2 className="dd-heading-md dd-mb-6">👤 Profile</h2>

            <form onSubmit={handleUpdateProfile}>
              <div className="dd-vstack dd-gap-4" style={{ maxWidth: '600px' }}>
                <div className="dd-profile-grid">
                  <div className="dd-form-field">
                    <label htmlFor="settings-display-name" className="dd-label">Display Name</label>
                    <Input
                      id="settings-display-name"
                      type="text"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      placeholder="Your display name"
                    />
                  </div>
                  <div className="dd-form-field">
                    <label htmlFor="settings-email" className="dd-label">Email Address</label>
                    <Input
                      id="settings-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your@email.com"
                    />
                  </div>
                </div>

                <div>
                  <p className="dd-label dd-mb-1">Username</p>
                  <p className="dd-text-secondary">{user?.username} <span className="dd-text-muted">(cannot be changed)</span></p>
                </div>

                <div>
                  <p className="dd-label dd-mb-1">Stats</p>
                  <div className="dd-hstack dd-gap-6">
                    <span>Level: <strong style={{ color: '#7c3aed' }}>{user?.level}</strong></span>
                    <span>Points: <strong style={{ color: '#059669' }}>{user?.total_points}</strong></span>
                  </div>
                </div>

                {profileError && <div className="dd-alert-error"><p>{profileError}</p></div>}
                {profileSuccess && <div className="dd-alert-success"><p>{profileSuccess}</p></div>}

                <Button type="submit" variant="primary" size="lg" loading={profileLoading}>
                  {profileLoading ? 'Updating…' : 'Update Profile'}
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* Security tab */}
        {activeTab === 'security' && (
          <div className="dd-vstack dd-gap-6 dd-mt-6">
            {/* Change Password */}
            <div className="dd-card">
              <h2 className="dd-heading-md dd-mb-4">🔒 Change Password</h2>
              <form onSubmit={handleChangePassword}>
                <div className="dd-vstack dd-gap-4" style={{ maxWidth: '500px' }}>
                  <div className="dd-form-field">
                    <label htmlFor="settings-current-pw" className="dd-label">Current Password</label>
                    <Input
                      id="settings-current-pw"
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder="Enter current password"
                    />
                  </div>
                  <div className="dd-form-field">
                    <label htmlFor="settings-new-pw" className="dd-label">New Password</label>
                    <Input
                      id="settings-new-pw"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="At least 10 characters"
                    />
                  </div>
                  <div className="dd-form-field">
                    <label htmlFor="settings-confirm-pw" className="dd-label">Confirm New Password</label>
                    <Input
                      id="settings-confirm-pw"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm new password"
                    />
                    {confirmPassword && newPassword && confirmPassword !== newPassword && (
                      <span style={{ fontSize: '0.75rem', color: '#dc2626', marginTop: '0.25rem' }}>
                        Passwords do not match
                      </span>
                    )}
                  </div>

                  {passwordError && <div className="dd-alert-error"><p>{passwordError}</p></div>}
                  {passwordSuccess && <div className="dd-alert-success"><p>{passwordSuccess}</p></div>}

                  <Button type="submit" variant="primary" size="lg" loading={passwordLoading}>
                    {passwordLoading ? 'Changing…' : 'Change Password'}
                  </Button>
                </div>
              </form>
            </div>

            {/* Two-Factor Authentication */}
            <div className="dd-card">
              <div className="dd-hstack dd-mb-4" style={{ justifyContent: 'space-between' }}>
                <h2 className="dd-heading-md">🔐 Two-Factor Authentication</h2>
                <Badge variant={twoFAEnabled ? 'success' : 'secondary'} size="lg">
                  {twoFAEnabled ? 'Enabled' : 'Disabled'}
                </Badge>
              </div>
              <p className="dd-text-secondary dd-mb-4">
                Add an extra layer of security with an authenticator app (Google Authenticator, Authy, 1Password).
              </p>
              <div className="dd-vstack dd-gap-4">
                {!twoFAEnabled && (
                  <div className="dd-hstack dd-gap-3">
                    <Button variant="primary" size="lg" onClick={startTwoFASetup} disabled={twoFALoading}>
                      {twoFALoading ? 'Setting up...' : 'Start 2FA Setup'}
                    </Button>
                    {twoFASecret && <Badge variant="info" size="lg">Secret Ready</Badge>}
                  </div>
                )}

                {twoFAQr && (
                  <div>
                    <label className="dd-label dd-mb-2">Scan QR Code</label>
                    <div className="dd-qr-box">
                      <img src={twoFAQr} alt="2FA QR code" style={{ width: '100%', borderRadius: '6px' }} />
                    </div>
                    <p className="dd-text-sm dd-text-secondary dd-mt-2">
                      Or use secret: <strong>{twoFASecret}</strong>
                    </p>
                  </div>
                )}

                {(twoFASecret || twoFAQr) && !twoFAEnabled && (
                  <div style={{ maxWidth: '320px' }}>
                    <label htmlFor="settings-2fa-code" className="dd-label dd-mb-2">Enter 6-digit code</label>
                    <div className="dd-hstack dd-gap-3">
                      <Input
                        id="settings-2fa-code"
                        value={twoFACode}
                        onChange={(e) => setTwoFACode(e.target.value)}
                        placeholder="123456"
                      />
                      <Button variant="success" onClick={handleVerifyTwoFA} disabled={twoFALoading}>
                        Verify
                      </Button>
                    </div>
                  </div>
                )}

                {twoFAEnabled && (
                  <Button variant="danger" size="lg" onClick={handleDisableTwoFA} disabled={twoFALoading}>
                    Disable 2FA
                  </Button>
                )}

                {twoFAStatus && <div className="dd-alert-success"><p>{twoFAStatus}</p></div>}
                {twoFAError && <div className="dd-alert-error"><p>{twoFAError}</p></div>}
              </div>
            </div>
          </div>
        )}

        {/* Appearance tab */}
        {activeTab === 'appearance' && (
          <div className="dd-card dd-mt-6">
            <h2 className="dd-heading-md dd-mb-6">🎨 Appearance</h2>
            <div className="dd-vstack dd-gap-6" style={{ maxWidth: '600px' }}>
              <div>
                <p className="dd-label dd-mb-1">Theme Mode</p>
                <p className="dd-text-sm dd-text-secondary dd-mb-3">Choose between light and dark mode</p>
                <div className="dd-hstack dd-gap-3">
                  <Button
                    variant={colorMode === 'light' ? 'primary' : 'outline'}
                    size="lg"
                    onClick={toggleColorMode}
                  >
                    ☀️ Light Mode
                  </Button>
                  <Button
                    variant={colorMode === 'dark' ? 'primary' : 'outline'}
                    size="lg"
                    onClick={toggleColorMode}
                  >
                    🌙 Dark Mode
                  </Button>
                </div>
                <p className="dd-text-xs dd-text-secondary dd-mt-2">
                  Current: <strong>{colorMode === 'light' ? 'Light' : 'Dark'}</strong>
                </p>
              </div>

              <div>
                <p className="dd-label dd-mb-1">Friends Feature</p>
                <p className="dd-text-sm dd-text-secondary dd-mb-3">Show or hide the friends functionality throughout the app</p>
                <div className="dd-hstack dd-gap-3">
                  <Button
                    variant={localStorage.getItem('hideFriends') !== 'true' ? 'success' : 'outline'}
                    size="lg"
                    onClick={() => { localStorage.setItem('hideFriends', 'false'); window.location.reload(); }}
                  >
                    👥 Show Friends
                  </Button>
                  <Button
                    variant={localStorage.getItem('hideFriends') === 'true' ? 'danger' : 'outline'}
                    size="lg"
                    onClick={() => { localStorage.setItem('hideFriends', 'true'); window.location.reload(); }}
                  >
                    🚫 Hide Friends
                  </Button>
                </div>
                <p className="dd-text-xs dd-text-secondary dd-mt-2">
                  Current: <strong>{localStorage.getItem('hideFriends') === 'true' ? 'Hidden' : 'Visible'}</strong>
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Task Settings tab */}
        {activeTab === 'task-settings' && (
          <div className="dd-vstack dd-gap-6 dd-mt-6">
            <div className="dd-settings-grid">
              {/* Categories */}
              <div className="dd-card">
                <h2 className="dd-heading-md dd-mb-2">📁 Categories</h2>
                <p className="dd-text-sm dd-text-secondary dd-mb-4">Organize tasks by type</p>
                <div className="dd-hstack dd-gap-2 dd-mb-4">
                  <Input
                    placeholder="New category name"
                    value={newCategory}
                    onChange={(e) => setNewCategory(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleAddCategory()}
                  />
                  <Button variant="primary" onClick={handleAddCategory}>Add</Button>
                </div>
                <div className="dd-vstack dd-gap-3">
                  {categories.map((category) => (
                    <div
                      key={category.id}
                      className="dd-settings-item"
                      style={{ opacity: category.is_active ? 1 : 0.5 }}
                    >
                      <Badge variant="primary">{category.name}</Badge>
                      {confirmDeleteCategory === category.id ? (
                        <div className="dd-hstack dd-gap-1">
                          <button className="dd-icon-btn dd-icon-btn-danger" onClick={() => handleDeleteCategory(category.id)}>✓</button>
                          <button className="dd-icon-btn" onClick={() => setConfirmDeleteCategory(null)}>✕</button>
                        </div>
                      ) : (
                        <button
                          className="dd-icon-btn"
                          onClick={() => handleDeleteCategory(category.id)}
                          aria-label={`Deactivate category ${category.name}`}
                        >
                          {category.is_active ? '✕' : 'Inactive'}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Statuses */}
              <div className="dd-card">
                <h2 className="dd-heading-md dd-mb-2">📊 Statuses</h2>
                <p className="dd-text-sm dd-text-secondary dd-mb-4">Track task progress stages</p>
                <div className="dd-hstack dd-gap-2 dd-mb-4">
                  <Input
                    placeholder="New status name"
                    value={newStatus}
                    onChange={(e) => setNewStatus(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleAddStatus()}
                  />
                  <Button variant="primary" onClick={handleAddStatus}>Add</Button>
                </div>
                <div className="dd-vstack dd-gap-3">
                  {statuses.map((status) => (
                    <div key={status} className="dd-settings-item">
                      <Badge variant="success">{status}</Badge>
                      {confirmDeleteStatus === status ? (
                        <div className="dd-hstack dd-gap-1">
                          <button className="dd-icon-btn dd-icon-btn-danger" onClick={() => handleDeleteStatus(status)}>✓</button>
                          <button className="dd-icon-btn" onClick={() => setConfirmDeleteStatus(null)}>✕</button>
                        </div>
                      ) : (
                        <button
                          className="dd-icon-btn"
                          onClick={() => handleDeleteStatus(status)}
                          aria-label={`Delete status ${status}`}
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Tags */}
              <div className="dd-card">
                <h2 className="dd-heading-md dd-mb-2">🏷️ Tags</h2>
                <p className="dd-text-sm dd-text-secondary dd-mb-4">Labels pulled from your tasks</p>
                <div className="dd-hstack dd-gap-2 dd-mb-4">
                  <Input
                    placeholder="New tag name"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleAddTag()}
                  />
                  <Button variant="primary" onClick={handleAddTag}>Add</Button>
                </div>
                {loadingTags ? (
                  <p className="dd-text-secondary">Loading tags...</p>
                ) : (
                  <div className="dd-vstack dd-gap-3">
                    {tags.length === 0 ? (
                      <p className="dd-text-sm dd-text-secondary dd-text-center dd-py-4">
                        No tags yet. Create tasks with tags to see them here.
                      </p>
                    ) : (
                      tags.map((tag) => (
                        <div key={tag} className="dd-settings-item">
                          <Badge variant="secondary">#{tag}</Badge>
                          {confirmDeleteTag === tag ? (
                            <div className="dd-hstack dd-gap-1">
                              <button className="dd-icon-btn dd-icon-btn-danger" onClick={() => handleDeleteTag(tag)}>✓</button>
                              <button className="dd-icon-btn" onClick={() => setConfirmDeleteTag(null)}>✕</button>
                            </div>
                          ) : (
                            <button
                              className="dd-icon-btn"
                              onClick={() => handleDeleteTag(tag)}
                              aria-label={`Remove tag ${tag}`}
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="dd-info-box">
              <h3 className="dd-heading-md">💡 How Task Settings Work</h3>
              <div className="dd-vstack dd-gap-2">
                <p>&bull; <strong>Categories:</strong> Organize tasks by type (work, personal, etc.)</p>
                <p>&bull; <strong>Statuses:</strong> Track task progress stages</p>
                <p>&bull; <strong>Tags:</strong> Flexible labels — auto-populated from your tasks</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Settings;
